import axios from 'axios';
import { API_CONFIG } from '../config';
import { getActiveAuthStorage, clearCsadminAuthStorage } from '../constants/authStorage';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const storage = getActiveAuthStorage();
    const token = localStorage.getItem(storage.token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const storage = getActiveAuthStorage();
        const refreshToken = localStorage.getItem(storage.refreshToken);
        if (refreshToken) {
          const response = await axios.post(`${API_CONFIG.BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { token: newToken } = response.data;
          localStorage.setItem(storage.token, newToken);

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // fall through to logout
      }

      clearCsadminAuthStorage();
      if (!window.location.pathname.includes('/login')) {
        window.location.href = API_CONFIG.loginPath;
      }
    }

    // Handle other errors
    if (error.response?.status === 403) {
      console.error('Access forbidden');
    } else if (error.response?.status === 404) {
      console.error('Resource not found');
    } else if (error.response?.status >= 500) {
      console.error('Server error');
    }

    return Promise.reject(error);
  }
);

// CS Admin API endpoints
export const csAdminApi = {
  // Authentication — portal-specific login endpoints
  login: (credentials) => api.post(`${API_CONFIG.AUTH_PATH}/login`, credentials),

  verifyCsadminOtp: ({ otpSessionId, otp }) =>
    api.post(`${API_CONFIG.AUTH_PATH}/verify-otp`, { otpSessionId, otp }),

  forgotPassword: ({ identifier }) =>
    api.post(`${API_CONFIG.AUTH_PATH}/forgot-password`, { identifier }),

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    return api.post('/auth/logout');
  },

  // User Management
  getProfile: () => {
    return api.get('/csadmin/profile');
  },

  // Dashboard
  getDashboardStats: (params) => {
    return api.get('/csadmin/dashboard/stats', { params });
  },

  // Declarations
  getDeclarations: (params) => {
    return api.get('/csadmin/declarations', { params });
  },

  getDeclarationById: (id) => {
    return api.get(`/csadmin/declarations/${id}`);
  },

  // Directors
  getDirectors: (params) => {
    return api.get('/csadmin/directors', { params });
  },

  getDirectorById: (id) => {
    return api.get(`/csadmin/directors/${id}`);
  },

  // Schedules
  getSchedules: (params) => {
    return api.get('/csadmin/schedules', { params });
  },

  getScheduleById: (id) => {
    return api.get(`/csadmin/schedules/${id}`);
  },

  // Reports
  getReports: (params) => {
    return api.get('/csadmin/reports', { params });
  },

  exportReport: (reportType, params) => {
    return api.get(`/csadmin/reports/export/${reportType}`, {
      params,
      responseType: 'blob',
    });
  },

  updateUserPreferences: (data) => api.put('/csadmin/profile/preferences', data),

  updateExpiryAlert: (id, data, file) => {
    if (file) {
      const form = new FormData();
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          form.append(key, value);
        }
      });
      form.append('file', file);
      return api.patch(`/csadmin/expiry-alerts/${id}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.patch(`/csadmin/expiry-alerts/${id}`, data);
  },

  getCompanies: (params) => api.get('/csadmin/companies', { params }),

  getCompanyById: (id) => api.get(`/csadmin/companies/${id}`),

  updateCompany: (id, data) => api.put(`/csadmin/companies/${id}`, data),

  getCompanySettings: (id) => api.get(`/csadmin/companies/${id}/settings`),

  updateCompanySettings: (id, data) => api.put(`/csadmin/companies/${id}/settings`, data),

  getCommittees: (params) => api.get('/csadmin/committees', { params }),

  getCommitteeById: (id) => api.get(`/csadmin/committees/${id}`),

  createCommittee: (data) => api.post('/csadmin/committees', data),

  getExpiryAlerts: (params) => api.get('/csadmin/expiry-alerts', { params }),

  createDirector: (formData) =>
    api.post('/directors/create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  assignDirectorFromPool: (directorId, data) =>
    api.post(`/csadmin/directors/${directorId}/assign-to-company`, data),

  createCompany: (data) => api.post('/csadmin/companies', data),

  createCompanySecretary: (data) => api.post('/csadmin/company-secretaries', data),

  updateCompanySecretary: (id, data) => api.put(`/csadmin/company-secretaries/${id}`, data),

  getCompanyShareholders: (companyId) =>
    api.get(`/csadmin/companies/${companyId}/shareholders`),

  createCompanyShareholder: (companyId, data) =>
    api.post(`/csadmin/companies/${companyId}/shareholders`, data),

  updateCompanyShareholder: (companyId, shareholderId, data) =>
    api.put(`/csadmin/companies/${companyId}/shareholders/${shareholderId}`, data),

  deleteCompanyShareholder: (companyId, shareholderId) =>
    api.delete(`/csadmin/companies/${companyId}/shareholders/${shareholderId}`),

  getCompanyOfficers: (companyId) =>
    api.get(`/csadmin/companies/${companyId}/officers`),

  createCompanyOfficer: (companyId, data) =>
    api.post(`/csadmin/companies/${companyId}/officers`, data),

  updateCompanyOfficer: (companyId, officerId, data) =>
    api.put(`/csadmin/companies/${companyId}/officers/${officerId}`, data),

  deleteCompanyOfficer: (companyId, officerId) =>
    api.delete(`/csadmin/companies/${companyId}/officers/${officerId}`),

  getCompanyAuditLog: (companyId, params) =>
    api.get(`/csadmin/companies/${companyId}/audit-log`, { params }),

  updateDirector: (id, data) => api.put(`/csadmin/directors/${id}`, data),

  setDirectorStatus: (id, isActive) =>
    api.patch(`/csadmin/directors/${id}/status`, { is_active: isActive }),

  endDirectorAppointment: (directorId, data) =>
    api.post(`/csadmin/directors/${directorId}/end-appointment`, data),

  endAllDirectorAppointments: (directorId, data) =>
    api.post(`/csadmin/directors/${directorId}/end-all-appointments`, data),

  deleteDirector: (id) => api.delete(`/csadmin/directors/${id}`),

  getCompanyDirectors: (companyId, params) =>
    api.get(`/csadmin/companies/${companyId}/directors`, { params }),

  getCompanyDocuments: (companyId) => api.get(`/csadmin/companies/${companyId}/documents`),

  getCompanySecretaries: (params) => api.get('/csadmin/company-secretaries', { params }),

  getCompanySecretaryById: (id) => api.get(`/csadmin/company-secretaries/${id}`),

  updateCompanySecretaryAssignments: (id, companyIds) =>
    api.put(`/csadmin/company-secretaries/${id}/assignments`, { companyIds }),

  resendCompanySecretaryWelcomeEmail: (id, data = {}) =>
    api.post(`/csadmin/company-secretaries/${id}/resend-welcome-email`, data),

  deleteCompanySecretary: (id) => api.delete(`/csadmin/company-secretaries/${id}`),

  getUsers: (params) => api.get('/csadmin/users', { params }),

  getRoles: () => api.get('/csadmin/users/roles'),

  getUserAuditLog: (params) => api.get('/csadmin/users/audit-log', { params }),

  createUser: (data) => api.post('/csadmin/users', data),

  updateUser: (id, data) => api.put(`/csadmin/users/${id}`, data),

  setUserStatus: (id, status) => api.patch(`/csadmin/users/${id}/status`, { status }),

  resetUserPassword: (id, newPassword) =>
    api.post(`/csadmin/users/${id}/reset-password`, newPassword ? { newPassword } : {}),

  backfillPersonRegistry: () => api.post('/csadmin/persons/backfill'),

  backfillPersonDocuments: () => api.post('/csadmin/persons/backfill-documents'),

  deleteUser: (id) => api.delete(`/csadmin/users/${id}`),

  getUserPermissions: (userId) => api.get(`/csadmin/users/${userId}/permissions`),
  updateUserPermissions: (userId, data) => api.put(`/csadmin/users/${userId}/permissions`, data),
  getPortalAccountPermissions: (portalAccountId) =>
    api.get(`/csadmin/company-secretaries/${portalAccountId}/permissions`),
  updatePortalAccountPermissions: (portalAccountId, data) =>
    api.put(`/csadmin/company-secretaries/${portalAccountId}/permissions`, data),
  getRoleDefaultPermissions: (role) => api.get(`/csadmin/permissions/role-defaults/${role}`),
  checkPermission: (params) => api.get('/csadmin/permissions/check', { params }),
  getCompanyPermissions: (companyId) => api.get(`/csadmin/companies/${companyId}/permissions`),
  updateCompanyUserPermissions: (companyId, userId, data) =>
    api.put(`/csadmin/companies/${companyId}/permissions/users/${userId}`, data),
  updateCompanyPortalAccountPermissions: (companyId, portalAccountId, data) =>
    api.put(`/csadmin/companies/${companyId}/permissions/portal-accounts/${portalAccountId}`, data),

  getOrgStructure: () => api.get('/csadmin/org-structure'),

  changePassword: (data) => api.post('/csadmin/profile/change-password', data),

  updateCommittee: (id, data) => api.put(`/csadmin/committees/${id}`, data),

  addCommitteeMember: (committeeId, data) =>
    api.post(`/csadmin/committees/${committeeId}/members`, data),

  updateCommitteeMember: (committeeId, memberId, data) =>
    api.put(`/csadmin/committees/${committeeId}/members/${memberId}`, data),

  removeCommitteeMember: (committeeId, memberId) =>
    api.delete(`/csadmin/committees/${committeeId}/members/${memberId}`),

  scheduleCommitteeMeeting: (committeeId, data) =>
    api.post(`/csadmin/committees/${committeeId}/meetings`, data),

  updateCommitteeMeeting: (committeeId, meetingId, data) =>
    api.put(`/csadmin/committees/${committeeId}/meetings/${meetingId}`, data),

  deleteCommitteeMeeting: (committeeId, meetingId) =>
    api.delete(`/csadmin/committees/${committeeId}/meetings/${meetingId}`),

  getDirectorDocuments: (directorId) => api.get(`/csadmin/directors/${directorId}/documents`),

  getDirectorAuditLog: (directorId, params) =>
    api.get(`/csadmin/directors/${directorId}/audit-log`, { params }),

  getDirectorActivities: (directorId, params) =>
    api.get(`/csadmin/directors/${directorId}/activities`, { params }),

  getDocuments: (ownerType, ownerId) =>
    api.get('/csadmin/documents', { params: { ownerType, ownerId } }),

  uploadDocument: (formData) =>
    api.post('/csadmin/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  getDocumentFileAccessToken: (fileUrl) =>
    api.post('/csadmin/documents/file-access-token', { fileUrl }),

  deleteDocument: (documentId) => api.delete(`/csadmin/documents/${documentId}`),

  updateDocument: (documentId, data) => api.patch(`/csadmin/documents/${documentId}`, data),

  getCommitteeAvailableDirectors: (committeeId) =>
    api.get(`/csadmin/committees/${committeeId}/available-directors`),

  getLookupCategories: () => api.get('/csadmin/lookups/categories'),

  getLookups: (params) => api.get('/csadmin/lookups', { params }),

  createLookup: (data) => api.post('/csadmin/lookups', data),

  updateLookup: (id, data) => api.put(`/csadmin/lookups/${id}`, data),

  // Digest Settings
  getDigestSettings: () => api.get('/digest-settings'),
  
  updateDigestSettings: (data) => api.put('/digest-settings', data),
  
  sendDigestNow: (data) => api.post('/digest/send-now', data),
  
  sendTestDigest: (data) => api.post('/digest/send-test', data),
};

export default api;
